import React, { useEffect, useState } from 'react';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = getBackendUrl() + '/api';

export const SplashScreen = ({ onComplete }) => {
  const [splashImage, setSplashImage] = useState(null);
  const [logo, setLogo] = useState(null);
  const [siteTitle, setSiteTitle] = useState('Dawal Connect');
  const [message, setMessage] = useState('');
  const BACKEND_URL = getBackendUrl();

  useEffect(() => {
    // Fetch site settings for logo
    fetch(`${API}/settings/site`)
      .then(res => res.json())
      .then(data => {
        if (data.site_logo) {
          // Prepend backend URL if the logo path is relative
          const logoUrl = data.site_logo.startsWith('http') 
            ? data.site_logo 
            : `${BACKEND_URL}${data.site_logo}`;
          setLogo(logoUrl);
        }
        if (data.site_title) {
          setSiteTitle(data.site_title);
        }
      })
      .catch(err => console.error('Error loading site settings:', err));

    // Fetch splash screen settings for background image and message
    fetch(`${API}/splash-screen-settings`)
      .then(res => res.json())
      .then(data => {
        if (data.splash_image) {
          // Prepend backend URL if the image path is relative
          const imageUrl = data.splash_image.startsWith('http') 
            ? data.splash_image 
            : `${BACKEND_URL}${data.splash_image}`;
          setSplashImage(imageUrl);
        }
        if (data.message) {
          setMessage(data.message);
        }
      })
      .catch(err => console.error('Error loading splash settings:', err));

    // Auto-hide after animations complete + 3 seconds display time
    // Logo animation takes 1.5s, then show for 3s, total = 4.5s
    const timer = setTimeout(() => {
      if (onComplete) onComplete();
    }, 4500);

    return () => clearTimeout(timer);
  }, [onComplete, BACKEND_URL]);

  return (
    <>
      <style>{`
        @keyframes spinIn {
          0% {
            transform: scale(0.3) rotate(0deg);
            opacity: 0;
          }
          50% {
            opacity: 0.5;
          }
          100% {
            transform: scale(1) rotate(360deg);
            opacity: 1;
          }
        }
      `}</style>
      
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 animate-in fade-in-0 duration-300">
      <div className="relative w-full h-full flex flex-col items-center justify-center p-8">
        {/* Background Image */}
        {splashImage && (
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ 
              backgroundImage: `url(${splashImage})`,
              filter: 'blur(8px)'
            }}
          />
        )}

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center animate-in zoom-in-95 slide-in-from-bottom-4 duration-1000">
          {/* Logo */}
          {logo && (
            <div className="mb-8 animate-in zoom-in-50 duration-1500">
              <img 
                src={logo} 
                alt="Logo" 
                className="w-32 h-32 md:w-40 md:h-40 object-contain drop-shadow-2xl"
                style={{
                  animation: 'spinIn 1.5s ease-out forwards'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Site Name */}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-8 drop-shadow-lg animate-in slide-in-from-bottom-4 duration-700 delay-200 text-center">
            {siteTitle}
          </h1>

          {/* Loading Animation */}
          <div className="flex gap-2 animate-in fade-in-0 duration-700 delay-500">
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>

        {/* Bottom Messages */}
        <div className="absolute bottom-8 text-center animate-in fade-in-0 duration-700 delay-700 px-6 w-full">
          {message && (
            <p className="text-2xl md:text-3xl font-bold text-white mb-3 drop-shadow-lg">
              {message}
            </p>
          )}
          <p className="text-sm text-slate-400">Loading your workspace...</p>
        </div>
      </div>
    </div>
    </>
  );
};

export default SplashScreen;
